import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { trackFrameworkViewed } from '@/lib/analytics-events'
import { ArrowLeft, ExternalLink, FileText } from 'lucide-react'
import { ApiError } from '@/lib/api'
import { triggerPrint } from '@/lib/export'
import { formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { FrameworkDetailHeader } from '@/components/regulatory/FrameworkDetailHeader'
import {
  useRegulatoryFrameworkDetail,
  type RegulatoryFrameworkDetailDeadline,
  type RegulatoryFrameworkDetailEnforcementAction,
  type RegulatoryRecentAmendment,
} from '@/hooks/useRegulatoryQueries'

export function RegulatoryFrameworkDetailPage() {
  const { slug } = useParams<{ slug: string }>()

  // KAN-32 regulatory:framework_viewed
  useEffect(() => {
    trackFrameworkViewed(slug)
  }, [slug])
  const { data, isLoading, isError, error, refetch } = useRegulatoryFrameworkDetail(slug)
  const [watched, setWatched] = useState(false)

  const isNotFound = error instanceof ApiError && error.status === 404

  if (isLoading) {
    return <RegulatoryFrameworkSkeleton />
  }

  if (isError) {
    if (isNotFound) {
      // After KAN-271 s4 the API only 404s for genuinely-absent frameworks —
      // an unknown slug or a soft-archived row (archived reads as removed). The
      // "planned" / "missing_brief" empty-states are now HTTP 200 union arms
      // handled below, not 404s.
      return (
        <FrameworkStateCard
          title="Framework not found"
          body="This framework doesn't exist or has been removed."
        />
      )
    }

    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-6 p-6">
        <ErrorState
          title="Could not load regulatory framework"
          message="The framework detail may be temporarily unavailable."
          onRetry={() => refetch()}
        />
      </main>
    )
  }

  if (!data) {
    return (
      <FrameworkStateCard
        title="Framework not found"
        body="This framework doesn't exist or has been removed."
      />
    )
  }

  // `state: "planned"` — detail_enabled=false. Tracked, not yet built out.
  if (data.state === 'planned') {
    return (
      <FrameworkStateCard
        title="Coming soon"
        body="We're tracking this framework and building out coverage. This page will be available once analysis is complete."
      />
    )
  }

  // `state: "missing_brief"` — enabled but no brief drafted yet.
  if (data.state === 'missing_brief') {
    return (
      <FrameworkStateCard
        title="Content in progress"
        body="We're preparing the regulatory analysis for this framework. Check back soon, or subscribe to regulatory alerts to be notified."
      />
    )
  }

  // `state: "ready"` — full detail surface.
  const { framework, brief, enforcement } = data
  const deadlines = data.deadlines.slice(0, 5)
  const recentActions = enforcement.recent.slice(0, 5)
  const pendingReview = data.coverageState === 'partial' || brief.needsReview

  return (
    <main
      className="mx-auto flex max-w-6xl flex-col gap-6 p-6"
      aria-label="Regulatory framework detail"
    >
      <nav aria-label="breadcrumb" className="text-xs text-muted-foreground">
        <Link
          to="/regulatory"
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          Regulatory radar
        </Link>
      </nav>

      {pendingReview ? (
        <section
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
        >
          <p className="font-semibold">Pending review</p>
          <p>
            This framework is visible as a partial brief while analyst review is
            still in progress. Treat coverage as directional.
          </p>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant={watched ? 'default' : 'outline'}
          size="sm"
          onClick={() => setWatched((next) => !next)}
        >
          {watched ? 'Watching' : 'Watch'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() =>
            triggerPrint({
              type: 'RegulatoryFramework',
              name: framework.name,
              slug: framework.slug,
            })
          }
        >
          <FileText className="h-4 w-4" aria-hidden />
          Lift to board pack
        </Button>
        <Button asChild variant="ghost" size="sm">
          <a
            href={framework.referenceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Open source
          </a>
        </Button>
      </div>

      <FrameworkDetailHeader framework={framework} brief={brief} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <div className="flex flex-col gap-6">
          <RecentAmendmentsCard amendments={brief.recentAmendments} />

          <Card>
            <CardHeader>
              <CardTitle>Analyst supplement</CardTitle>
              <CardDescription>
                Public analyst notes and coverage rationale.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm leading-relaxed">
              {brief.analystPublicNotes ? (
                <p className="whitespace-pre-wrap">{brief.analystPublicNotes}</p>
              ) : (
                <p className="text-muted-foreground">No public notes yet.</p>
              )}
              {brief.coverageRationale ? (
                <div className="rounded-md border bg-muted/20 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Coverage rationale
                  </p>
                  <p className="mt-1">{brief.coverageRationale}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <aside className="flex flex-col gap-6">
          <DeadlinesCard deadlines={deadlines} />
          <EnforcementCard
            frameworkSlug={framework.slug}
            count90d={enforcement.count90d}
            latestDecisionAt={enforcement.latestDecisionAt}
            actions={recentActions}
          />
        </aside>
      </div>
    </main>
  )
}

function RecentAmendmentsCard({ amendments }: { amendments: RegulatoryRecentAmendment[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent amendments</CardTitle>
        <CardDescription>Latest published changes to the framework.</CardDescription>
      </CardHeader>
      <CardContent>
        {amendments.length > 0 ? (
          <div className="space-y-3">
            {amendments.map((amendment) => (
              <a
                key={`${amendment.date}-${amendment.headline}`}
                href={amendment.source_url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-md border p-3 hover:border-primary"
              >
                <p className="text-sm font-medium">{amendment.headline}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatDate(amendment.date)}
                </p>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No recent amendments published.</p>
        )}
      </CardContent>
    </Card>
  )
}

function DeadlinesCard({ deadlines }: { deadlines: RegulatoryFrameworkDetailDeadline[] }) {
  return (
    <Card role="region" aria-label="Upcoming deadlines">
      <CardHeader>
        <CardTitle>Upcoming deadlines</CardTitle>
        <CardDescription>Next five per-framework regulatory dates.</CardDescription>
      </CardHeader>
      <CardContent>
        {deadlines.length > 0 ? (
          <div className="space-y-3">
            {deadlines.map((deadline) => (
              <a
                key={deadline.id}
                href={deadline.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="block rounded-md border p-3 hover:border-primary"
              >
                <p className="text-sm font-medium">{deadline.label}</p>
                <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                  {deadline.kind.replace(/_/g, ' ')} · {formatDate(deadline.dueDate)}
                </p>
                {deadline.description ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {deadline.description}
                  </p>
                ) : null}
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No upcoming deadlines published.</p>
        )}
      </CardContent>
    </Card>
  )
}

function EnforcementCard({
  frameworkSlug,
  count90d,
  latestDecisionAt,
  actions,
}: {
  frameworkSlug: string
  count90d: number
  latestDecisionAt: string | null
  actions: RegulatoryFrameworkDetailEnforcementAction[]
}) {
  const actionsTarget = `/regulatory-actions?framework=${encodeURIComponent(frameworkSlug)}`

  return (
    <Card role="region" aria-label="Recent enforcement">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Recent enforcement</CardTitle>
            <CardDescription>
              {count90d} actions in 90 days · latest {formatDate(latestDecisionAt)}
            </CardDescription>
          </div>
          <Link
            to={actionsTarget}
            className="shrink-0 text-sm font-medium text-primary hover:underline"
          >
            View all actions
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {actions.length > 0 ? (
          <div className="space-y-3">
            {actions.map((action) => {
              const body = (
                <>
                  <p className="text-sm font-medium">{action.organisationName}</p>
                  <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                    {action.jurisdiction ?? 'Unknown jurisdiction'} ·{' '}
                    {formatDate(action.decisionDate)}
                    {action.severity ? ` · ${action.severity}` : ''}
                  </p>
                  {action.summary ? (
                    <p className="mt-2 text-sm text-muted-foreground">{action.summary}</p>
                  ) : null}
                </>
              )

              return action.sourceUrl ? (
                <a
                  key={action.id}
                  href={action.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-md border p-3 hover:border-primary"
                >
                  {body}
                </a>
              ) : (
                <div key={action.id} className="rounded-md border p-3">
                  {body}
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No recent enforcement actions published.</p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Single empty/terminal-state card. KAN-271 collapses the prior
 * `FrameworkNotFound` variants into one presentational shell driven entirely
 * by the caller's copy, covering all three non-`ready` surfaces:
 *  - "Coming soon"        → `state: "planned"` (detail_enabled=false)
 *  - "Content in progress" → `state: "missing_brief"`
 *  - "Framework not found" → HTTP 404 (unknown slug or archived)
 */
function FrameworkStateCard({ title, body }: { title: string; body: string }) {
  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-4 p-6">
      <Link
        to="/regulatory"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        Regulatory radar
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
      </Card>
    </main>
  )
}

function RegulatoryFrameworkSkeleton() {
  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-6 p-6">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-36 w-full" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Skeleton className="h-96 w-full" />
        <div className="space-y-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </main>
  )
}
